import { useEffect, useMemo, useRef, useState } from "react";
import { SearchIcon, XIcon } from "lucide-react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { AssetScreenerFilters } from "./AssetScreenerFilters";
import { AssetScreenerSortInput, SortOptions } from "./AssetScreenerSortInput";
import type { ScreenerAsset } from "./types";
import { useAssetFilters } from "./useAssetFilters";
import { AssetResultRow, dataRoomPillClassName } from "./AssetResultRow";
import { assetHasCorpus } from "~/modules/asset_chat/assets";
import * as Analytics from "../../analytics";
import { useUser } from "~/modules/user/hooks/useUser";
import { SectionHeader } from "~/components/SectionHeader";

// // // //

/**
 * Rough collapsed-row height used to seed the virtualizer before each row is
 * measured. Actual heights are reported back via `measureElement`, so this only
 * affects the very first paint and the scrollbar estimate for un-rendered rows.
 */
const ESTIMATED_ROW_HEIGHT = 152;
const ESTIMATED_HEADER_HEIGHT = 48;

/**
 * The results list is virtualized as a single flat sequence, so section
 * headers are list items too — a discriminated union keeps one virtualizer
 * driving both row types.
 */
type ScreenerListItem =
    | {
          type: "header";
          key: string;
          title: string;
          hasDataRoomBadge: boolean;
          count: number;
          // The first header of the list doubles as the toolbar hosting the
          // sort control, so sorting stays reachable in every layout mode.
          showSort: boolean;
          // Headers that follow a section of rows need breathing room above
          // them; the header that opens the list does not.
          hasTopSpacing: boolean;
      }
    | {
          type: "asset";
          key: number;
          asset: ScreenerAsset;
          isFirstRow: boolean;
          isLastRow: boolean;
          // Id of the previous asset in the same section, used to re-draw this
          // row's top border while that neighbor is expanded (and detached).
          previousAssetId: number | null;
      };

function toAssetItems(assets: ScreenerAsset[]): ScreenerListItem[] {
    return assets.map((asset, index) => ({
        type: "asset",
        key: asset.id,
        asset,
        isFirstRow: index === 0,
        isLastRow: index === assets.length - 1,
        previousAssetId: index > 0 ? assets[index - 1].id : null,
    }));
}

function PageSection(props: {
    title: string;
    hasDataRoomBadge: boolean;
    count: number;
    sortBy?: string;
    onSortByChange?: (value: string) => void;
    className?: string;
}) {
    const {
        title,
        hasDataRoomBadge,
        count,
        sortBy,
        onSortByChange,
        className,
    } = props;

    return (
        <div className={className}>
            <SectionHeader label={title}>
                <div className="flex w-full flex-wrap items-center gap-3 md:ml-3 md:min-w-0 md:flex-1">
                    {hasDataRoomBadge && (
                        <span
                            className={cn(
                                dataRoomPillClassName,
                                "rounded-md border whitespace-nowrap",
                            )}
                        >
                            Data Room Available
                        </span>
                    )}
                    <span className="text-sm font-normal text-tertiary-foreground">
                        {count.toLocaleString()} asset{count !== 1 ? "s" : ""}
                    </span>
                    {sortBy != null && onSortByChange && (
                        <div className="ml-auto w-full sm:w-auto sm:min-w-[180px] sm:max-w-[240px]">
                            <AssetScreenerSortInput
                                showLabel={false}
                                value={sortBy}
                                onChange={onSortByChange}
                            />
                        </div>
                    )}
                </div>
            </SectionHeader>
        </div>
    );
}

export function AssetScreenerListingPage(props: {
    assets: ScreenerAsset[];
    variant?: "default" | "home";
}) {
    const { assets = [], variant = "default" } = props;
    const isHomeVariant = variant === "home";
    const { isLoggedIn } = useUser();

    const {
        searchQuery,
        setSearchQuery,
        selectedAssetClasses,
        setSelectedAssetClasses,
        yieldRange,
        setYieldRange,
        selectedRedemption,
        setSelectedRedemption,
        selectedNetworks,
        setSelectedNetworks,
        nonKycOnly,
        setNonKycOnly,
        sortBy,
        setSortBy,
        allYields,
        maxYield,
        assetClassOptions,
        redemptionOptions,
        networkOptions,
        filteredAssets,
        activeFilterCount,
        initialExpandedAssetId,
        clearFilters,
    } = useAssetFilters(assets);

    // Expansion state lives here (not inside each row) so a row keeps its
    // expanded/collapsed state when the virtualizer unmounts and remounts it
    // while scrolling. A Set allows multiple rows to stay expanded at once.
    const [expandedAssetIds, setExpandedAssetIds] = useState<Set<number>>(
        () => new Set(),
    );

    // Sort moved from the filters bar into the list's first header row; keep
    // emitting the same analytics event it fired there.
    const handleSortByChange = (value: string) => {
        Analytics.trackFilterSortBy(value);
        setSortBy(value as SortOptions);
    };

    const toggleExpandedAsset = (assetId: number) => {
        setExpandedAssetIds((current) => {
            const next = new Set(current);
            if (next.has(assetId)) {
                next.delete(assetId);
            } else {
                next.add(assetId);
            }
            return next;
        });
    };

    // Collapse every row when the result set changes.
    useEffect(() => {
        setExpandedAssetIds(new Set());
    }, [
        searchQuery,
        selectedAssetClasses,
        yieldRange,
        selectedRedemption,
        selectedNetworks,
        nonKycOnly,
        sortBy,
    ]);

    // Seed the deep-linked asset as expanded on first load. This effect is
    // declared after the collapse-on-change effect above so that, in the render
    // where the deep link applies its filters (which triggers that collapse),
    // this expansion runs last and wins.
    useEffect(() => {
        if (initialExpandedAssetId == null) {
            return;
        }

        setExpandedAssetIds((current) => {
            const next = new Set(current);
            next.add(initialExpandedAssetId);
            return next;
        });
    }, [initialExpandedAssetId]);

    // Split the (already filtered + sorted) results into a data-room section
    // followed by everything else, with a header item above each. The split
    // only applies under the default "Data Room Available" sort — choosing any
    // other sort means the user asked for a specific global order, so the
    // sections collapse into one flat list that honors it. Either way the list
    // opens with a header row: it carries the section title/count and hosts
    // the sort control.
    //
    // Home renders one flat list instead: the data-room deals still sort to the
    // top, but without being sectioned off, and its header lives above the
    // filters rather than inside the list.
    const listItems = useMemo<ScreenerListItem[]>(() => {
        if (isHomeVariant) {
            return toAssetItems(filteredAssets);
        }

        const dataRoomAssets: ScreenerAsset[] = [];
        const otherAssets: ScreenerAsset[] = [];
        filteredAssets.forEach((asset) => {
            if (assetHasCorpus(asset)) {
                dataRoomAssets.push(asset);
            } else {
                otherAssets.push(asset);
            }
        });

        const isSectioned =
            sortBy === SortOptions.dataRoom && dataRoomAssets.length > 0;

        if (!isSectioned) {
            return [
                {
                    type: "header",
                    key: "header-all",
                    title: "Assets",
                    hasDataRoomBadge: false,
                    count: filteredAssets.length,
                    showSort: true,
                    hasTopSpacing: false,
                },
                ...toAssetItems(filteredAssets),
            ];
        }

        const items: ScreenerListItem[] = [
            {
                type: "header",
                key: "header-data-room",
                title: "Assets",
                hasDataRoomBadge: true,
                count: dataRoomAssets.length,
                showSort: true,
                hasTopSpacing: false,
            },
            ...toAssetItems(dataRoomAssets),
        ];

        if (otherAssets.length > 0) {
            items.push(
                {
                    type: "header",
                    key: "header-other",
                    title: "All other assets",
                    hasDataRoomBadge: false,
                    count: otherAssets.length,
                    showSort: false,
                    hasTopSpacing: true,
                },
                ...toAssetItems(otherAssets),
            );
        }

        return items;
    }, [filteredAssets, isHomeVariant, sortBy]);

    // The page itself is the scroll container (no inner overflow wrapper), so we
    // virtualize against the window. `scrollMargin` tells the virtualizer how
    // far the list sits from the top of the document; we track it because the
    // filters above the list change height as filters are applied.
    const listRef = useRef<HTMLDivElement | null>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const measureScrollMargin = () => {
            const node = listRef.current;
            if (node) {
                setScrollMargin(
                    node.getBoundingClientRect().top + window.scrollY,
                );
            }
        };

        measureScrollMargin();

        // Observe the document so we recompute when content above the list (the
        // filters) grows or shrinks, and when the list remounts after the empty
        // state.
        const resizeObserver = new ResizeObserver(measureScrollMargin);
        resizeObserver.observe(document.body);

        return () => resizeObserver.disconnect();
    }, []);

    const virtualizer = useWindowVirtualizer({
        count: listItems.length,
        estimateSize: (index) =>
            listItems[index].type === "header"
                ? ESTIMATED_HEADER_HEIGHT
                : ESTIMATED_ROW_HEIGHT,
        overscan: 5,
        scrollMargin,
        getItemKey: (index) => listItems[index].key,
    });

    const virtualItems = virtualizer.getVirtualItems();

    // On the deep-linked first load, scroll the auto-expanded row to the top of
    // the viewport so its full card is visible without any manual scrolling.
    // The list is virtualized, so we drive the scroll through the virtualizer
    // (by index) rather than reaching for a DOM node that may not be mounted.
    // A ref keeps this to the initial load only.
    const hasScrolledToInitial = useRef(false);
    useEffect(() => {
        if (hasScrolledToInitial.current || initialExpandedAssetId == null) {
            return;
        }

        const index = listItems.findIndex(
            (item) =>
                item.type === "asset" &&
                item.asset.id === initialExpandedAssetId,
        );
        if (index < 0) {
            return;
        }
        hasScrolledToInitial.current = true;
        virtualizer.scrollToIndex(index, { align: "start" });
    }, [initialExpandedAssetId, listItems, virtualizer]);

    return (
        <div className="min-h-screen">
            <div
                className={cn("space-y-4", {
                    "pt-3 pb-6": !isHomeVariant,
                    "pt-0": isHomeVariant,
                })}
            >
                {isHomeVariant && (
                    <PageSection
                        title="Asset Screener"
                        hasDataRoomBadge={false}
                        count={filteredAssets.length}
                        sortBy={sortBy}
                        onSortByChange={handleSortByChange}
                    />
                )}

                <div
                    className={cn("mx-auto w-full", {
                        "max-w-6xl": !isHomeVariant,
                    })}
                >
                    <AssetScreenerFilters
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        yieldRange={yieldRange}
                        onYieldRangeChange={setYieldRange}
                        allYields={allYields}
                        maxYield={maxYield}
                        selectedRedemption={selectedRedemption}
                        onRedemptionChange={setSelectedRedemption}
                        selectedNetworks={selectedNetworks}
                        onNetworksChange={setSelectedNetworks}
                        networkOptions={networkOptions}
                        nonKycOnly={nonKycOnly}
                        onNonKycChange={setNonKycOnly}
                        selectedAssetClasses={selectedAssetClasses}
                        onAssetClassesChange={setSelectedAssetClasses}
                        assetClassOptions={assetClassOptions}
                        redemptionOptions={redemptionOptions}
                        activeFilterCount={activeFilterCount}
                        onClearFilters={clearFilters}
                    />
                </div>

                <div
                    className={cn("mx-auto w-full", {
                        "max-w-6xl": !isHomeVariant,
                    })}
                >
                    {filteredAssets.length > 0 ? (
                        <div
                            ref={listRef}
                            className="relative w-full"
                            style={{ height: virtualizer.getTotalSize() }}
                        >
                            {virtualItems.map((virtualItem) => {
                                const index = virtualItem.index;
                                const item = listItems[index];

                                return (
                                    <div
                                        key={virtualItem.key}
                                        data-index={index}
                                        ref={virtualizer.measureElement}
                                        className="absolute top-0 left-0 w-full"
                                        style={{
                                            transform: `translateY(${
                                                virtualItem.start - scrollMargin
                                            }px)`,
                                        }}
                                    >
                                        {item.type === "header" ? (
                                            <PageSection
                                                title={item.title}
                                                className={cn({
                                                    "pt-8": item.hasTopSpacing,
                                                })}
                                                hasDataRoomBadge={
                                                    item.hasDataRoomBadge
                                                }
                                                count={item.count}
                                                sortBy={
                                                    item.showSort
                                                        ? sortBy
                                                        : undefined
                                                }
                                                onSortByChange={
                                                    item.showSort
                                                        ? handleSortByChange
                                                        : undefined
                                                }
                                            />
                                        ) : (
                                            <AssetResultRow
                                                asset={item.asset}
                                                isLoggedIn={isLoggedIn}
                                                isExpanded={expandedAssetIds.has(
                                                    item.asset.id,
                                                )}
                                                hasTopBorder={
                                                    item.isFirstRow ||
                                                    (item.previousAssetId !=
                                                        null &&
                                                        expandedAssetIds.has(
                                                            item.previousAssetId,
                                                        ))
                                                }
                                                isFirstRow={item.isFirstRow}
                                                isLastRow={item.isLastRow}
                                                onToggle={() =>
                                                    toggleExpandedAsset(
                                                        item.asset.id,
                                                    )
                                                }
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="mb-4 text-muted-foreground">
                                <SearchIcon className="h-12 w-12" />
                            </div>
                            <p className="mb-1 text-base font-normal text-secondary-foreground">
                                No funds found
                            </p>
                            <p className="mb-4 text-sm text-tertiary-foreground">
                                Try adjusting your filters to see more results
                            </p>
                            {activeFilterCount > 0 && (
                                <Button
                                    type="button"
                                    onClick={() => {
                                        Analytics.trackFilterClearAll(
                                            activeFilterCount,
                                        );
                                        clearFilters();
                                    }}
                                    variant="outline"
                                    className="rounded-md border-border bg-card px-4 py-2 text-sm font-medium text-secondary-foreground shadow-xs hover:bg-tertiary hover:text-foreground"
                                >
                                    <XIcon className="mr-2 h-4 w-4 shrink-0" />
                                    Clear filters
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
